import { useState, useEffect, useCallback } from 'react'
import * as Tabs from '@radix-ui/react-tabs'
import { useAuth } from '@/contexts/AuthContext'
import { Building2, TrendingUp, Award } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useToast } from '@/components/ui/MobileToast'
import { businessService } from '@/services/businessService'
import { aiService } from '@/services/aiService'
import { BasicInfoTab } from './tabs/BasicInfoTab'
import { FinancialDataTab } from './tabs/FinancialDataTab'
import { OperationalDataTab } from './tabs/OperationalDataTab'
import { ComplianceTab } from './tabs/ComplianceTab'
import { DocumentsTab } from './tabs/DocumentsTab'

const TAB_STORAGE_KEY = 'vendor-profile-tab'
const DEFAULT_TAB = 'basic-info'

const TABS = [
  { value: 'basic-info', label: 'Basic Info' },
  { value: 'financial-data', label: 'Financial Data' },
  { value: 'operational-data', label: 'Operational Data' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'documents', label: 'Documents' },
]

function getStoredTab(): string {
  try {
    return sessionStorage.getItem(TAB_STORAGE_KEY) || DEFAULT_TAB
  } catch {
    return DEFAULT_TAB
  }
}

function storeTab(tab: string): void {
  try {
    sessionStorage.setItem(TAB_STORAGE_KEY, tab)
  } catch {
    // sessionStorage unavailable — gracefully ignore
  }
}

export function BusinessProfilePage() {
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [business, setBusiness] = useState<any>(null)
  const [activeTab, setActiveTab] = useState(getStoredTab)
  const { showToast } = useToast()

  const loadBusinessProfile = useCallback(async () => {
    setIsLoading(true)
    try {
      if (user?.businessId) {
        const businessData = await businessService.getBusinessById(user.businessId)
        setBusiness(businessData)
        return
      }

      // Fallback: get first business for demo
      const response = await businessService.getBusinesses({ limit: 1 })
      if (response.businesses.length > 0) {
        setBusiness(response.businesses[0])
      }
    } catch (error) {
      console.error('Failed to load business profile:', error)
      showToast({
        type: 'error',
        title: 'Load Failed',
        message: 'Unable to load business profile.',
      })
    } finally {
      setIsLoading(false)
    }
  }, [user?.businessId, showToast])

  useEffect(() => {
    loadBusinessProfile()
  }, [loadBusinessProfile])

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    storeTab(value)
  }

  const handleBusinessUpdate = useCallback((updated: any) => {
    setBusiness(updated)
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading profile...</span>
      </div>
    )
  }

  if (!business) {
    return <CreateBusinessForm onCreated={(biz) => { setBusiness(biz); loadBusinessProfile(); }} />
  }

  const getBandColor = (band: string) => {
    switch (band) {
      case 'A+':
      case 'A': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
      case 'B+':
      case 'B': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900'
      case 'C+':
      case 'C': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
      default: return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Business Profile
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage your business information and view your TLBH score
        </p>
      </div>

      {/* Score Card */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-lg p-8 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-2">{business.business_name}</h2>
            <p className="text-primary-100 mb-4">{business.industry_sector}</p>
            <div className="flex items-center gap-4">
              {business.band && (
                <div className={`px-4 py-2 rounded-full font-semibold ${getBandColor(business.band)}`}>
                  Band {business.band}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                <span>Confidence: {parseFloat(business.confidence || '0').toFixed(0)}%</span>
              </div>
            </div>
          </div>
          <div className="mt-6 md:mt-0 text-center">
            <p className="text-primary-100 text-sm mb-2">TLBH Score</p>
            <p className="text-6xl font-bold">{parseFloat(business.score || '0').toFixed(2)}</p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">
                Updated {new Date(business.calculated_at || business.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
        <Tabs.List className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          {TABS.map((tab) => (
            <Tabs.Trigger
              key={tab.value}
              value={tab.value}
              className="px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 border-transparent text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:border-primary-300 data-[state=active]:text-primary-600 data-[state=active]:dark:text-primary-400 data-[state=active]:border-primary-600 data-[state=active]:dark:border-primary-400"
            >
              {tab.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <div className="mt-6">
          <Tabs.Content value="basic-info">
            <BasicInfoTab business={business} onUpdate={handleBusinessUpdate} />
          </Tabs.Content>

          <Tabs.Content value="financial-data">
            <FinancialDataTab business={business} onUpdate={handleBusinessUpdate} />
          </Tabs.Content>

          <Tabs.Content value="operational-data">
            <OperationalDataTab business={business} onUpdate={handleBusinessUpdate} />
          </Tabs.Content>

          <Tabs.Content value="compliance">
            <ComplianceTab business={business} onUpdate={handleBusinessUpdate} />
          </Tabs.Content>

          <Tabs.Content value="documents">
            <DocumentsTab business={business} onUpdate={handleBusinessUpdate} />
          </Tabs.Content>
        </div>
      </Tabs.Root>
    </div>
  )
}


/**
 * Form for creating a new business profile.
 * Shown when a user has no business linked to their account.
 */
function CreateBusinessForm({ onCreated }: { onCreated: (business: any) => void }) {
  const { user, updateUser } = useAuth()
  const { showToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [_aiScore, setAiScore] = useState<any>(null)
  const [form, setForm] = useState({
    pan: '',
    business_name: '',
    business_type: 'private_limited',
    industry_sector: '',
    contact_email: user?.email || '',
    contact_phone: '',
    registered_address: '',
    annual_turnover: '',
    employee_count: '',
    gstin: '',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.pan || !form.business_name) {
      showToast({ type: 'error', title: 'Required', message: 'PAN and Business Name are required.' })
      return
    }

    setIsSubmitting(true)
    try {
      const business = await businessService.createBusiness({
        ...form,
        annual_turnover: form.annual_turnover ? parseFloat(form.annual_turnover) : undefined,
        employee_count: form.employee_count ? parseInt(form.employee_count) : undefined,
      })

      showToast({ type: 'success', title: 'Profile Created', message: 'Your business profile has been created!' })

      // Update auth context with new businessId
      if (business.id) {
        updateUser({ businessId: business.id })
      }

      // Trigger AI score generation
      try {
        const scoreResult = await aiService.getScore(business.id)
        setAiScore(scoreResult.data)
      } catch (err) {
        console.warn('AI score generation pending:', err)
      }

      onCreated(business)
    } catch (error: any) {
      showToast({
        type: 'error',
        title: 'Creation Failed',
        message: error.message || 'Unable to create business profile. Please check your details.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <Building2 className="w-12 h-12 text-primary-600 mx-auto mb-3" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create Your Business Profile</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Fill in your business details to generate your TLBH Score instantly.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* PAN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              PAN <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="pan"
              value={form.pan}
              onChange={handleChange}
              placeholder="ABCDE1234F"
              maxLength={10}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white uppercase"
              required
            />
          </div>

          {/* GSTIN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">GSTIN</label>
            <input
              type="text"
              name="gstin"
              value={form.gstin}
              onChange={handleChange}
              placeholder="22ABCDE1234F1Z5"
              maxLength={15}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white uppercase"
            />
          </div>

          {/* Business Name */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Business Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="business_name"
              value={form.business_name}
              onChange={handleChange}
              placeholder="Your Business Pvt Ltd"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          {/* Business Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Business Type</label>
            <select
              name="business_type"
              value={form.business_type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="private_limited">Private Limited</option>
              <option value="public_limited">Public Limited</option>
              <option value="partnership">Partnership</option>
              <option value="sole_proprietorship">Sole Proprietorship</option>
              <option value="llp">LLP</option>
              <option value="opc">OPC</option>
            </select>
          </div>

          {/* Industry */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Industry Sector</label>
            <input
              type="text"
              name="industry_sector"
              value={form.industry_sector}
              onChange={handleChange}
              placeholder="e.g., Technology, Manufacturing"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Contact Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Email</label>
            <input
              type="email"
              name="contact_email"
              value={form.contact_email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Contact Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Phone</label>
            <input
              type="tel"
              name="contact_phone"
              value={form.contact_phone}
              onChange={handleChange}
              placeholder="+91 9876543210"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Address */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Registered Address</label>
            <input
              type="text"
              name="registered_address"
              value={form.registered_address}
              onChange={handleChange}
              placeholder="Full business address"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Annual Turnover */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Annual Turnover (₹)</label>
            <input
              type="number"
              name="annual_turnover"
              value={form.annual_turnover}
              onChange={handleChange}
              placeholder="e.g., 5000000"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Employee Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employee Count</label>
            <input
              type="number"
              name="employee_count"
              value={form.employee_count}
              onChange={handleChange}
              placeholder="e.g., 25"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-md shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner size="sm" />
              Creating Profile & Generating Score...
            </span>
          ) : (
            'Create Profile & Generate TLBH Score'
          )}
        </button>
      </form>
    </div>
  )
}
